import React from 'react';
import { Phone, Wrench, Droplet, Sun, Wind, Tv, Snowflake, Hammer, Paintbrush, Grid3x3, Camera, Droplets, Flame, Star } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';

const Home = () => {
  const services = [
    { icon: Wrench, title: 'Electrical Work', description: 'Expert electrical repairs and installations' },
    { icon: Droplet, title: 'Plumbing Work', description: 'Professional plumbing solutions' },
    { icon: Sun, title: 'Solar System', description: 'Installation & maintenance services' },
    { icon: Wind, title: 'Heating & Cooling', description: 'HVAC system solutions' },
    { icon: Tv, title: 'Home Appliances', description: 'Repair all home appliances' },
    { icon: Snowflake, title: 'AC Services', description: 'Installation & maintenance' },
    { icon: Hammer, title: 'Carpentry', description: 'Quality wood work services' },
    { icon: Paintbrush, title: 'Painting', description: 'Professional painting & renovation' },
    { icon: Grid3x3, title: 'Tiling Work', description: 'Tiling & civil repair services' },
    { icon: Camera, title: 'CCTV Systems', description: 'Security system installation' },
    { icon: Star, title: 'Glass & Aluminum', description: 'Expert glass & aluminum work' },
    { icon: Droplets, title: 'Water Tank & Motor', description: 'Installation & repair services' },
    { icon: Flame, title: 'Geyser & Boiler', description: 'Complete geyser services' }
  ];

  const handleCall = (number) => {
    window.location.href = `tel:${number}`;
  };

  const handleWhatsApp = (number) => {
    window.open(`https://wa.me/${number.replace(/-/g, '')}`, '_blank');
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-2">
              <Wrench className="w-8 h-8 text-emerald-600" />
              <h1 className="text-2xl font-bold text-gray-900">A&M Service</h1>
            </div>
            <div className="hidden md:flex space-x-8">
              <a href="#services" className="text-gray-700 hover:text-emerald-600 transition-colors">Services</a>
              <a href="#contact" className="text-gray-700 hover:text-emerald-600 transition-colors">Contact</a>
            </div>
            <Button 
              onClick={() => handleCall('03005041535')} 
              className="bg-emerald-600 hover:bg-emerald-700 transition-all duration-300"
            >
              <Phone className="w-4 h-4 mr-2" />
              Call Now
            </Button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative py-32 px-4 sm:px-6 lg:px-8 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img 
            src="https://images.unsplash.com/photo-1676311396794-f14881e9daaa" 
            alt="Professional home maintenance tools"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/60 to-black/50"></div>
        </div>
        <div className="max-w-7xl mx-auto text-center relative z-10">
          <h2 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 drop-shadow-lg">
            Professional Home Maintenance
            <span className="block text-emerald-400 mt-2">At Your Doorstep</span>
          </h2>
          <p className="text-xl text-white/90 mb-8 max-w-3xl mx-auto drop-shadow-md">
            Reliable technicians | Quality service | Timely response
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              size="lg" 
              onClick={() => handleCall('03005041535')}
              className="bg-emerald-600 hover:bg-emerald-700 text-lg px-8 py-6 transition-all duration-300 hover:scale-105 shadow-xl"
            >
              <Phone className="w-5 h-5 mr-2" />
              Call 0300-5041535
            </Button>
            <Button 
              size="lg" 
              onClick={() => handleWhatsApp('03008582703')}
              variant="outline"
              className="border-2 border-white text-white hover:bg-white hover:text-emerald-600 text-lg px-8 py-6 transition-all duration-300 hover:scale-105 shadow-xl"
            >
              <Phone className="w-5 h-5 mr-2" />
              WhatsApp 0300-8582703
            </Button>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">Our Services</h2>
            <p className="text-lg text-gray-600">Complete home maintenance solutions under one roof</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service, index) => {
              const Icon = service.icon;
              return (
                <Card key={index} className="group hover:shadow-xl transition-all duration-300 hover:-translate-y-1 border border-gray-200">
                  <CardContent className="p-6">
                    <div className="flex items-start space-x-4">
                      <div className="bg-emerald-100 p-3 rounded-lg group-hover:bg-emerald-600 transition-colors duration-300">
                        <Icon className="w-6 h-6 text-emerald-600 group-hover:text-white transition-colors duration-300" />
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900 mb-2">{service.title}</h3>
                        <p className="text-gray-600 text-sm">{service.description}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-slate-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">Why Choose A&M Service?</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="relative overflow-hidden rounded-xl shadow-lg group">
              <img 
                src="https://images.unsplash.com/photo-1682345262055-8f95f3c513ea" 
                alt="Professional electrician at work"
                className="w-full h-64 object-cover group-hover:scale-110 transition-transform duration-300"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent"></div>
              <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                <Star className="w-8 h-8 mb-3" />
                <h3 className="text-xl font-semibold mb-2">Professional Technicians</h3>
                <p className="text-white/90">Skilled and certified experts for all services</p>
              </div>
            </div>
            <div className="relative overflow-hidden rounded-xl shadow-lg group">
              <img 
                src="https://images.unsplash.com/photo-1749532125405-70950966b0e5" 
                alt="Professional plumber at work"
                className="w-full h-64 object-cover group-hover:scale-110 transition-transform duration-300"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent"></div>
              <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                <Phone className="w-8 h-8 mb-3" />
                <h3 className="text-xl font-semibold mb-2">Quick Response</h3>
                <p className="text-white/90">Fast and reliable service at your doorstep</p>
              </div>
            </div>
            <div className="relative overflow-hidden rounded-xl shadow-lg group">
              <img 
                src="https://images.unsplash.com/photo-1716395300732-3b36a3be038e" 
                alt="Professional maintenance technician"
                className="w-full h-64 object-cover group-hover:scale-110 transition-transform duration-300"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent"></div>
              <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                <Wrench className="w-8 h-8 mb-3" />
                <h3 className="text-xl font-semibold mb-2">Quality Work</h3>
                <p className="text-white/90">High-quality service with customer satisfaction</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 px-4 sm:px-6 lg:px-8 bg-emerald-600 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-bold mb-4">Ready to Get Started?</h2>
          <p className="text-xl mb-8 text-emerald-50">Contact us today for reliable home maintenance services</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              size="lg" 
              onClick={() => handleCall('03005041535')}
              className="bg-white text-emerald-600 hover:bg-gray-100 text-lg px-8 py-6 transition-all duration-300 hover:scale-105"
            >
              <Phone className="w-5 h-5 mr-2" />
              0300-5041535
            </Button>
            <Button 
              size="lg" 
              onClick={() => handleWhatsApp('03008582703')}
              className="bg-white text-emerald-600 hover:bg-gray-100 text-lg px-8 py-6 transition-all duration-300 hover:scale-105"
            >
              <Phone className="w-5 h-5 mr-2" />
              0300-8582703
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div>
              <div className="flex items-center space-x-2 mb-4">
                <Wrench className="w-6 h-6 text-emerald-600" />
                <h3 className="text-xl font-bold">A&M Service</h3>
              </div>
              <p className="text-gray-400">Professional home maintenance at your doorstep</p>
            </div>
            <div>
              <h4 className="text-lg font-semibold mb-4">Quick Links</h4>
              <ul className="space-y-2">
                <li><a href="#services" className="text-gray-400 hover:text-emerald-600 transition-colors">Services</a></li>
                <li><a href="#contact" className="text-gray-400 hover:text-emerald-600 transition-colors">Contact</a></li>
              </ul>
            </div>
            <div>
              <h4 className="text-lg font-semibold mb-4">Contact Us</h4>
              <ul className="space-y-2 text-gray-400">
                <li className="flex items-center space-x-2">
                  <Phone className="w-4 h-4" />
                  <span>0300-5041535</span>
                </li>
                <li className="flex items-center space-x-2">
                  <Phone className="w-4 h-4" />
                  <span>0300-8582703</span>
                </li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-800 mt-8 pt-8 text-center text-gray-400">
            <p>&copy; 2025 A&M Service. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Home;